import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPut } from "./EmailApi";
import "./SharedMailboxesSection.css";

const defaultForm = {
  businessEmailId: "",
  mailboxName: "",
  members: "",
  accessLevel: "team",
};

const parseMembers = (value = "") => {
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

function SharedMailboxesSection() {
  const [emails, setEmails] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [editMap, setEditMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedEmailExists = useMemo(() => {
    if (!form.businessEmailId) return false;

    return emails.some((item) => String(item.id) === String(form.businessEmailId));
  }, [emails, form.businessEmailId]);

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [emailsRes, mailboxRes] = await Promise.all([
        apiGet("/api/email/my-emails"),
        apiGet("/api/email/shared-mailboxes"),
      ]);

      if (emailsRes?.success === false) {
        throw new Error(emailsRes?.message || "Failed to load business emails.");
      }

      if (mailboxRes?.success === false) {
        throw new Error(mailboxRes?.message || "Failed to load shared mailboxes.");
      }

      setEmails(Array.isArray(emailsRes?.data) ? emailsRes.data : []);
      setMailboxes(Array.isArray(mailboxRes?.data) ? mailboxRes.data : []);
    } catch (err) {
      setEmails([]);
      setMailboxes([]);
      setError(err.message || "Shared mailbox fetch failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    const intervalId = window.setInterval(fetchData, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchData]);

  const validateCreateForm = () => {
    if (!form.businessEmailId) return "Please select a business email.";
    if (!selectedEmailExists) return "Selected business email is not valid.";
    if (!form.mailboxName.trim()) return "Mailbox name is required.";
    if (form.mailboxName.trim().length < 3) {
      return "Mailbox name must be at least 3 characters.";
    }

    return "";
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    const validationError = validateCreateForm();

    setMessage("");
    setError("");

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setCreating(true);

      const data = await apiPost("/api/email/shared-mailboxes", {
        businessEmailId: form.businessEmailId,
        mailboxName: form.mailboxName.trim(),
        members: parseMembers(form.members),
        accessLevel: form.accessLevel,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to create shared mailbox.");
      }

      setMessage(data.message || "Shared mailbox created successfully.");
      setForm(defaultForm);
      fetchData();
    } catch (err) {
      setError(err.message || "Failed to create shared mailbox.");
    } finally {
      setCreating(false);
    }
  };

  const handleEditChange = (id, field, value) => {
    setEditMap((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const handleUpdate = async (id) => {
    const payload = editMap[id];

    if (!payload) {
      setError("Please update at least one field.");
      setMessage("");
      return;
    }

    try {
      setUpdatingId(String(id));
      setError("");
      setMessage("");

      const members =
        typeof payload.members === "string"
          ? parseMembers(payload.members)
          : undefined;

      const data = await apiPut(`/api/email/shared-mailboxes/${id}`, {
        ...payload,
        ...(payload.mailboxName
          ? { mailboxName: payload.mailboxName.trim() }
          : {}),
        ...(payload.members !== undefined ? { members } : {}),
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to update shared mailbox.");
      }

      setMessage(data.message || "Shared mailbox updated successfully.");

      setEditMap((prev) => {
        const nextMap = { ...prev };
        delete nextMap[id];
        return nextMap;
      });

      fetchData();
    } catch (err) {
      setError(err.message || "Failed to update shared mailbox.");
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <div className="email-panel-card smb-section">
      <div className="smb-header">
        <div>
          <span className="smb-badge">Team Email</span>
          <h2>Shared Mailboxes</h2>
          <p>
            Create team inboxes, assign members, and manage access levels for
            support, sales, and operations.
          </p>
        </div>

        <div className="smb-icon-card">👥</div>
      </div>

      {error ? <output className="email-error-box smb-alert error">{error}</output> : null}
      {message ? <output className="email-success-box smb-alert success">{message}</output> : null}

      <form className="email-panel-form smb-form" onSubmit={handleCreate}>
        <select
          value={form.businessEmailId}
          onChange={(event) => updateForm("businessEmailId", event.target.value)}
          aria-label="Select business email"
          required
        >
          <option value="">Select Business Email</option>
          {emails.map((item) => (
            <option key={item.id} value={item.id}>
              {item.email_address || item.emailAddress || "Business Email"}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Mailbox Name"
          value={form.mailboxName}
          onChange={(event) => updateForm("mailboxName", event.target.value)}
          autoComplete="off"
          required
        />

        <input
          type="text"
          placeholder="Members comma separated"
          value={form.members}
          onChange={(event) => updateForm("members", event.target.value)}
          autoComplete="off"
        />

        <select
          value={form.accessLevel}
          onChange={(event) => updateForm("accessLevel", event.target.value)}
          aria-label="Access level"
        >
          <option value="team">Team</option>
          <option value="manager">Manager</option>
          <option value="admin">Admin</option>
        </select>

        <button type="submit" disabled={creating || loading}>
          {creating ? "Creating..." : "Create Shared Mailbox"}
        </button>
      </form>

      <div className="smb-summary-row">
        <div>
          <strong>{mailboxes.length}</strong>
          <span>Shared Mailboxes</span>
        </div>
        <div>
          <strong>{emails.length}</strong>
          <span>Business Emails</span>
        </div>
        <div>
          <strong>10s</strong>
          <span>Auto Refresh</span>
        </div>
      </div>

      <div className="email-panel-list smb-list">
        {loading ? (
          <p className="smb-empty">Loading shared mailboxes...</p>
        ) : mailboxes.length === 0 ? (
          <p className="smb-empty">No shared mailboxes found.</p>
        ) : (
          mailboxes.map((item) => {
            const mailboxId = item.id;
            const currentEdit = editMap[mailboxId] || {};
            const isUpdating = updatingId === String(mailboxId);

            return (
              <div className="email-panel-item smb-item" key={mailboxId}>
                <div className="smb-item-top">
                  <div>
                    <h4>{item.mailbox_name || item.mailboxName || "Shared Mailbox"}</h4>
                    <p>Email: {item.email_address || item.emailAddress || "-"}</p>
                  </div>

                  <span className="smb-status">{item.status || "active"}</span>
                </div>

                <div className="smb-meta-grid">
                  <p>Access: {item.access_level || item.accessLevel || "-"}</p>
                  <p>
                    Members:{" "}
                    {Array.isArray(item.members) && item.members.length
                      ? item.members.join(", ")
                      : "-"}
                  </p>
                </div>

                <div className="email-panel-inline-form smb-inline-form">
                  <input
                    type="text"
                    placeholder="Update mailbox name"
                    value={currentEdit.mailboxName || ""}
                    onChange={(event) =>
                      handleEditChange(mailboxId, "mailboxName", event.target.value)
                    }
                    autoComplete="off"
                  />

                  <input
                    type="text"
                    placeholder="Update members"
                    value={currentEdit.members || ""}
                    onChange={(event) =>
                      handleEditChange(mailboxId, "members", event.target.value)
                    }
                    autoComplete="off"
                  />

                  <select
                    value={currentEdit.accessLevel || ""}
                    onChange={(event) =>
                      handleEditChange(mailboxId, "accessLevel", event.target.value)
                    }
                    aria-label="Update access level"
                  >
                    <option value="">Select Access</option>
                    <option value="team">Team</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => handleUpdate(mailboxId)}
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SharedMailboxesSection;